import React, { useEffect, useState } from 'react'
import { Typography } from '@material-ui/core'
import { send_command, ADMIN_GET_APP_CLIENTS } from 'components/api'
import { TheChart } from '../TheChart'
import { ClientKeyTable } from '../components/ClientKeyTable'
import { ClientStatusBanner } from '../components/ClientStatusBanner'
import { EventStreamPanel } from '../components/EventStreamPanel'
import { useTenant } from '../context/TenantContext'

function OverviewStat({ value, label }) {
    return (
        <div className="superadmin-overview-stat">
            <div className="superadmin-overview-stat__value">{value}</div>
            <div className="superadmin-overview-stat__label">{label}</div>
        </div>
    )
}

function SectionLabel({ children }) {
    return <div className="sa-section-label">{children}</div>
}

export function TenantOverviewTab() {
    const { tenantId, tenantName, details, reload } = useTenant()
    const [spidStates, setSpidStates] = useState(null)
    const [spidLoading, setSpidLoading] = useState(false)
    const [surveyCount, setSurveyCount] = useState(null)
    const [client, setClient] = useState(null)
    const [clientLoading, setClientLoading] = useState(true)

    useEffect(() => {
        setSpidLoading(true)
        setClient(null)
        setClientLoading(true)
        send_command('ADMIN_SPID_STATES', { tenant_id: tenantId })
            .then(states => {
                setSpidStates(states)
                setSpidLoading(false)
            })
            .catch(() => setSpidLoading(false))

        send_command('ADMIN_GET_TENANT_SURVEYS', { tenant_id: tenantId })
            .then(surveys => setSurveyCount(Array.isArray(surveys) ? surveys.length : null))
            .catch(() => {})

        ADMIN_GET_APP_CLIENTS(tenantId)
            .then(clients => {
                setClient(Array.isArray(clients) && clients.length > 0 ? clients[0] : null)
                setClientLoading(false)
            })
            .catch(() => setClientLoading(false))
    }, [tenantId])

    const tenantDets = details?.tenant_details || {}
    const emailCount = Array.isArray(details?.emails) ? details.emails.length : '—'
    const langs = tenantDets.languages
    const langCount = langs
        ? (Array.isArray(langs) ? langs.length : Object.keys(langs).length)
        : '—'
    const locCount = Array.isArray(tenantDets.locations) ? tenantDets.locations.length : '—'
    const clientKeys = details?.client_keys || []

    const spidStateOrder = ['Created', 'Invited', 'Responded', 'Disabled']
    const spidEntries = spidStates
        ? [
            ...spidStateOrder
                .filter(s => spidStates[s] !== undefined)
                .map(s => [s, spidStates[s]]),
            ...Object.entries(spidStates).filter(([s]) => !spidStateOrder.includes(s)),
          ]
        : []
    const spidTotal = spidEntries.reduce((sum, [, n]) => sum + n, 0)

    const clientAppPath = `/superadmin/tenants/${tenantId}/client-apps`

    return (
        <div className="superadmin-tenant-overview">
            <ClientStatusBanner
                client={client}
                clientLoading={clientLoading}
                clientAppPath={clientAppPath}
            />

            <div className="sa-two-col">
                <div className="superadmin-card superadmin-overview-panel">
                    <SectionLabel>
                        SPIDs by state
                        {spidEntries.length > 0 && (
                            <span className="superadmin-overview-panel__meta">{spidTotal} total</span>
                        )}
                    </SectionLabel>
                    {spidLoading && (
                        <Typography variant="body2" color="textSecondary">Loading…</Typography>
                    )}
                    {!spidLoading && spidEntries.length > 0 && (
                        <div className="superadmin-overview-stat-grid">
                            {spidEntries.map(([state, count]) => (
                                <OverviewStat key={state} value={count} label={state} />
                            ))}
                        </div>
                    )}
                    {!spidLoading && spidStates && spidEntries.length === 0 && (
                        <Typography variant="body2" color="textSecondary">No SPIDs found</Typography>
                    )}
                </div>

                <div className="superadmin-card superadmin-overview-panel">
                    <SectionLabel>Tenant resources</SectionLabel>
                    <div className="superadmin-overview-stat-grid superadmin-overview-stat-grid--resources">
                        <OverviewStat label="Emails" value={emailCount} />
                        <OverviewStat label="Surveys" value={surveyCount !== null ? surveyCount : '—'} />
                        <OverviewStat label="Languages" value={langCount} />
                        <OverviewStat label="Locations" value={locCount} />
                    </div>
                </div>
            </div>

            <div className="superadmin-card superadmin-overview-panel">
                <SectionLabel>
                    Client keys
                    {clientKeys.length > 0 && (
                        <span className="superadmin-overview-panel__meta">{clientKeys.length}</span>
                    )}
                </SectionLabel>
                <ClientKeyTable tenantId={tenantId} initialKeys={clientKeys} onChange={reload} />
            </div>

            <div className="superadmin-card superadmin-overview-panel">
                <SectionLabel>Heartbeat activity (7 days)</SectionLabel>
                <TheChart tenant_name={tenantName} tenant_id={tenantId} />
            </div>

            <div className="superadmin-card superadmin-overview-panel">
                <SectionLabel>Event stream</SectionLabel>
                <EventStreamPanel
                    tenantId={tenantId}
                    hint="Watch integration lifecycle events and heartbeats while deploying or validating the client app."
                />
            </div>
        </div>
    )
}
